// src/components/hub/work/TaskListView.js
import React, { useMemo } from "react";
import InboxRow from "./InboxRow";
import { EmptyState } from "../../kit";
import { BUCKET_DESCRIPTIONS, statusPillClass } from "../constants";
import { fmtCount } from "../../../shared/format";

// ─── Done-item detection (work-page-fixes §A) ──────────────────────────────────
// An item is "done" (closed/completed) when its bucket is the done bucket or its
// status is a closed status. The status enum's closed statuses are "done" and
// "canceled"; "completed"/"closed"/"resolved" are included to match the emerald
// pill classification in InboxRow's statusPillClass so a visually-done row is
// always counted as done here too. Done items only enter the list when "Include
// done" is on (the done-bucket supplement fetch runs only then), so a non-empty
// done partition IS the signal to render the divider — no includeDone prop is
// needed (TaskListView does not receive one).
const DONE_STATUSES = new Set(["done", "canceled", "completed", "closed", "resolved"]);
const isDoneItem = (item) =>
  item?.bucket === "done" ||
  DONE_STATUSES.has(String(item?.status || "").toLowerCase());

// A grouped section is a "done" section when its key is a closed status or every
// item it holds is done. Used to push completed groups below the "Completed"
// divider (Step 2). Mixed groups (some active, some done — e.g. group-by
// due-date/client) stay with the active groups; their done rows are still
// visually muted via InboxRow (PART B).
const isDoneGroup = (group) =>
  group?.key === "done" ||
  group?.key === "canceled" ||
  (Array.isArray(group?.items) && group.items.length > 0 && group.items.every(isDoneItem));

// Section break between active and done items: a "Completed · {count}" label
// with a subtle horizontal rule. Only rendered when done items are present.
const CompletedDivider = ({ count }) => (
  <div
    className="flex items-center gap-3 py-2 px-1 select-none"
    role="separator"
    aria-label={`Completed · ${fmtCount(count)}`}
  >
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
      Completed · {fmtCount(count)}
    </span>
    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
  </div>
);

const TaskListView = ({
  activeCache,
  groups,
  collapsedGroups,
  toggleGroup,
  selectedIds,
  toggleSelect,
  onSelectAll,
  clearSelection,
  onBulkAction,
  selectedTaskId,
  setSelectedTask,
  onAssign,
  onMove,
  onArchive,
  onEdit,
  onDelete,
  onAssigneeClick,
  onDueDateChange,
  onClientClick,
  activeBucket,
  expandedSubtasks,
  subtaskCache,
  onToggleSubtasks,
  onAddSubtask,
  onDeleteSubtask,
  onSubtaskCountChange,
  isLoading,
  isSearchLoading,
  listError,
  hasActiveFilters,
  onRetry,
  setRetryNonce,
  subtaskRevision,
}) => {
  // ─── Select-all-visible state ───────────────────────────────────────────────
  // Visible items = ungrouped list (groups === null) or every non-collapsed
  // group's items. Collapsed groups are excluded so "select all" matches what's
  // on screen, per the tracker. The handler in WorkPage toggles: select-all if
  // not every visible id is selected, otherwise clear.
  const visibleItems = useMemo(() => {
    if (groups === null) return activeCache.items || [];
    const out = [];
    for (const g of groups) {
      if (collapsedGroups[g.key]) continue;
      for (const item of g.items || []) out.push(item);
    }
    return out;
  }, [groups, activeCache.items, collapsedGroups]);

  // ─── Active/done partition (work-page-fixes §A) ──────────────────────────────
  // Flat list: split activeCache.items into active + done so done items render
  // after a "Completed" divider. Grouped list: split `groups` into activeGroups
  // + doneGroups so completed sections render last behind the divider. Done
  // items only appear when "Include done" is on, so a non-empty done partition
  // is the sole signal to render the divider (no includeDone prop needed).
  const { activeItems, doneItems } = useMemo(() => {
    const active = [];
    const done = [];
    for (const item of activeCache.items || []) {
      if (isDoneItem(item)) done.push(item);
      else active.push(item);
    }
    return { activeItems: active, doneItems: done };
  }, [activeCache.items]);

  const { activeGroups, doneGroups, doneGroupItemCount } = useMemo(() => {
    const active = [];
    const done = [];
    let count = 0;
    for (const g of groups || []) {
      if (isDoneGroup(g)) { done.push(g); count += (g.items || []).length; }
      else active.push(g);
    }
    return { activeGroups: active, doneGroups: done, doneGroupItemCount: count };
  }, [groups]);

  const visibleCount = visibleItems.length;
  const selectedVisibleCount = useMemo(
    () => visibleItems.reduce((acc, item) => acc + (selectedIds.has(item.id) ? 1 : 0), 0),
    [visibleItems, selectedIds]
  );
  const allSelected = visibleCount > 0 && selectedVisibleCount === visibleCount;

  // ─── Empty-state copy ──────────────────────────────────────────────────────
  // Distinguishes a genuine empty bucket from a search/filter that matched
  // nothing. With active filters we say so; otherwise we fall back to the
  // per-bucket description (e.g. "Nobody has claimed this work …"). Renders
  // inside the list container via the shared EmptyState component — never at
  // the page level (work-page-fixes §B Item 3).
  const emptyMessage = hasActiveFilters
    ? "No work items match your filters."
    : BUCKET_DESCRIPTIONS[activeBucket] || "No work items found.";

  const inboxRowProps = (item) => ({
    item,
    isSelected: selectedIds.has(item.id),
    isActive: selectedTaskId === item.id,
    onSelect: setSelectedTask,
    onToggleSelect: toggleSelect,
    onAssign,
    onMove,
    onArchive,
    onEdit,
    onDelete,
    onAssigneeClick,
    onDueDateChange,
    onClientClick,
    bucket: activeBucket,
    expandedSubtasks,
    subtaskCache,
    onToggleSubtasks,
    onAddSubtask,
    onDeleteSubtask,
    onSubtaskCountChange,
    revision: subtaskRevision,
  });

  // ─── Group renderer (work-page-fixes §A) ─────────────────────────────────────
  // Renders one collapsible grouped section. Hoisted out of the grouped branch
  // so the active-groups block and the done-groups block share identical
  // styling — done groups simply render after the "Completed" divider.
  const renderGroup = (group) => {
    const groupItems = group.items || [];
    if (groupItems.length === 0) return null;
    const collapsed = !!collapsedGroups[group.key];
    return (
      <div key={group.key || "ungrouped"}>
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className="w-full flex items-center gap-2 px-3 py-1.5 mb-1 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
        >
          {collapsed ? (
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusPillClass(group.key)}`}>
            {group.displayName}
          </span>
          <span className="text-gray-400 dark:text-gray-500">{fmtCount(groupItems.length)}</span>
        </button>
        {!collapsed && (
          <div className="space-y-2">
            {groupItems.map((item) => (
              <InboxRow key={item.id} {...inboxRowProps(item)} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Error state ──────────────────────────────────────────────────────────
  if (listError) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-6">
        <svg className="w-10 h-10 text-red-400 dark:text-red-500 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Failed to load work items</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm break-words">{listError}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-[#32B1FF] text-white hover:bg-[#1a9ce6] transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Retry
        </button>
      </div>
    );
  }

  // ─── Loading skeletons ────────────────────────────────────────────────────
  if (isLoading && activeCache.items.length === 0) {
    return (
      <div className="space-y-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  // ─── Flat list (no grouping) ──────────────────────────────────────────────
  return (
    <div className={isLoading ? "opacity-60" : "opacity-100 transition-opacity"}>
      {isSearchLoading && (
        <div className="h-0.5 bg-blue-500 animate-pulse rounded-full w-full mb-2" />
      )}
      {/* ─── Bulk-action toolbar ────────────────────────────────────────────
          Hidden by default — the list is clean with no select-all row when
          nothing is chosen. Renders ONLY when ≥1 item is selected: a "{N}
          selected" count, a × quick-clear button, an explicit Select all /
          Deselect all text button, and Reassign / Status / Delete actions.
          The select-all affordance is a labeled button (not a stateful
          checkbox) so its meaning is unambiguous — a checkbox next to the
          count read as a per-item toggle and surprised users (clicking it
          selected all instead of clearing). Detached from the task rows as
          its own muted gray bordered card. Replaces the old fixed
          SelectionToast (removed from WorkPage). */}
      {selectedIds.size > 0 && visibleCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 border-y border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-800/50 text-xs font-semibold text-gray-500 dark:text-gray-400">
          <button
            type="button"
            onClick={() => onSelectAll(visibleItems.map((i) => i.id))}
            className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title={allSelected ? "Deselect all" : "Select all"}
            aria-label={allSelected ? "Deselect all" : "Select all"}
          >
            {allSelected ? (
              <svg className="w-3.5 h-3.5 text-[#32B1FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" />
                <path d="M8.5 12.5l2.5 2.5 4.5-5.5" stroke="white" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" />
              </svg>
            )}
          </button>
          <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">{fmtCount(selectedIds.size)} selected</span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 ml-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded p-1"
            title="Clear selection"
            aria-label="Clear selection"
          >
            ×
          </button>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => onBulkAction("assign")}
              className="font-medium text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white px-2 py-0.5 rounded"
            >
              Reassign
            </button>
            <button
              type="button"
              onClick={() => onBulkAction("move")}
              className="font-medium text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white px-2 py-0.5 rounded"
            >
              Status
            </button>
            <button
              type="button"
              onClick={() => onBulkAction("delete")}
              className="font-medium text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 py-0.5 rounded"
            >
              Delete
            </button>
          </div>
        </div>
      )}
      {groups === null ? (
        activeCache.items.length === 0 ? (
          <EmptyState message={emptyMessage} padding="py-16" />
        ) : (
          /* ─── Flat list (work-page-fixes §A: active items first, then a
              "Completed" divider, then done items) ─────────────────────────── */
          <div className="space-y-2">
            {activeItems.map((item) => (
              <InboxRow key={item.id} {...inboxRowProps(item)} />
            ))}
            {doneItems.length > 0 && <CompletedDivider count={doneItems.length} />}
            {doneItems.map((item) => (
              <InboxRow key={item.id} {...inboxRowProps(item)} />
            ))}
          </div>
        )
      ) : groups.length === 0 ? (
        <EmptyState message={emptyMessage} padding="py-16" />
      ) : (
        /* ─── Grouped sections (work-page-fixes §A: done groups render last,
            behind a "Completed" divider) ──────────────────────────────────── */
        <div className="space-y-3">
          {activeGroups.map(renderGroup)}
          {doneGroups.length > 0 && (
            <CompletedDivider count={doneGroupItemCount} />
          )}
          {doneGroups.map(renderGroup)}
        </div>
      )}
    </div>
  );
};

export default TaskListView;
